import { useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { ArrowRight, CheckCircle2 } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { Link } from 'react-router-dom';
import { z } from 'zod';
import { services } from '@/data/services';
import { Button } from './Button';

export interface ContactFormPayload {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  service: string;
  message: string;
  privacy: boolean;
}

interface ContactFormProps {
  variant?: 'compact' | 'full';
  tone?: 'default' | 'dark';
  onSubmit?: (data: ContactFormPayload) => Promise<void> | void;
  className?: string;
}

const phonePattern = /^[+]?[(]?[0-9]{2,4}[)]?[-\s./0-9]{5,}$/;

const createSchema = (variant: 'compact' | 'full') =>
  z
    .object({
      firstName: z
        .string()
        .trim()
        .min(
          2,
          variant === 'compact' ? 'Unesite ime i prezime.' : 'Unesite ime.'
        )
        .max(80, 'Ime je predugačko.'),
      lastName: z.string().trim().max(60, 'Prezime je predugačko.'),
      email: z
        .string()
        .trim()
        .min(1, 'Unesite email adresu.')
        .email('Unesite validnu email adresu.')
        .max(120, 'Email adresa je predugačka.'),
      phone: z
        .string()
        .trim()
        .min(1, 'Unesite broj telefona.')
        .refine(
          value => phonePattern.test(value),
          'Unesite validan broj telefona.'
        ),
      service: z.string().min(1, 'Izaberite temu ili uslugu.'),
      message: z
        .string()
        .trim()
        .min(20, 'Poruka treba da ima najmanje 20 karaktera.')
        .max(1200, 'Poruka može imati najviše 1200 karaktera.'),
      privacy: z.boolean()
    })
    .superRefine((data, context) => {
      if (
        variant === 'compact' &&
        data.firstName.split(/\s+/).filter(Boolean).length < 2
      ) {
        context.addIssue({
          code: 'custom',
          message: 'Unesite ime i prezime.',
          path: ['firstName']
        });
      }
      if (variant === 'full' && data.lastName.trim().length < 2) {
        context.addIssue({
          code: 'custom',
          message: 'Unesite prezime.',
          path: ['lastName']
        });
      }
      if (!data.privacy) {
        context.addIssue({
          code: 'custom',
          message: 'Potrebno je da prihvatite politiku privatnosti.',
          path: ['privacy']
        });
      }
    });

interface FieldErrorProps {
  id: string;
  message?: string;
}

function FieldError({ id, message }: FieldErrorProps) {
  if (!message) return null;
  return (
    <p id={id} className="mt-1.5 text-sm text-error" role="alert">
      {message}
    </p>
  );
}

export function ContactForm({
  variant = 'full',
  tone = 'default',
  onSubmit,
  className = ''
}: ContactFormProps) {
  const [submitted, setSubmitted] = useState(false);
  const reduceMotion = useReducedMotion();
  const schema = createSchema(variant);
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting }
  } = useForm<ContactFormPayload>({
    resolver: zodResolver(schema),
    mode: 'onTouched',
    reValidateMode: 'onChange',
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      service: '',
      message: '',
      privacy: false
    }
  });

  const submitForm = async (data: ContactFormPayload) => {
    setSubmitted(false);
    if (onSubmit) {
      await onSubmit(data);
    } else {
      // API integration point: replace this no-op with the email/backend request.
      await Promise.resolve(data);
    }
    setSubmitted(true);
    reset();
  };

  const isFull = variant === 'full';
  const inputClass =
    tone === 'dark'
      ? 'min-h-12 w-full rounded-[var(--radius-sm)] border border-white/18 bg-white/6 px-4 text-[0.95rem] text-footer-text outline-none transition-[border-color,background-color,box-shadow] placeholder:text-footer-muted hover:border-white/30 focus:border-primary focus:bg-white/9 focus:ring-2 focus:ring-primary/25 aria-invalid:border-error aria-invalid:focus:ring-error/20'
      : 'min-h-12 w-full rounded-[var(--radius-sm)] border border-border bg-input px-4 text-[0.95rem] text-text-primary outline-none transition-[border-color,box-shadow] placeholder:text-text-muted hover:border-border-strong focus:border-primary focus:ring-2 focus:ring-primary/18 aria-invalid:border-error aria-invalid:focus:ring-error/20';

  return (
    <form
      className={`w-full ${tone === 'dark' ? '[&_.form-label]:text-footer-text' : ''} ${className}`}
      onSubmit={handleSubmit(submitForm)}
      noValidate
    >
      <div className="grid gap-5 sm:grid-cols-2">
        <div className={isFull ? '' : 'sm:col-span-2'}>
          <label className="form-label" htmlFor={`${variant}-firstName`}>
            {isFull ? 'Ime' : 'Ime i prezime'} <span aria-hidden="true">*</span>
          </label>
          <input
            id={`${variant}-firstName`}
            className={inputClass}
            placeholder={isFull ? 'Vaše ime' : 'Unesite ime i prezime'}
            autoComplete={isFull ? 'given-name' : 'name'}
            aria-invalid={Boolean(errors.firstName)}
            aria-describedby={
              errors.firstName ? `${variant}-firstName-error` : undefined
            }
            {...register('firstName')}
          />
          <FieldError
            id={`${variant}-firstName-error`}
            message={errors.firstName?.message}
          />
        </div>

        {isFull && (
          <div>
            <label className="form-label" htmlFor="full-lastName">
              Prezime <span aria-hidden="true">*</span>
            </label>
            <input
              id="full-lastName"
              className={inputClass}
              placeholder="Vaše prezime"
              autoComplete="family-name"
              aria-invalid={Boolean(errors.lastName)}
              aria-describedby={
                errors.lastName ? 'full-lastName-error' : undefined
              }
              {...register('lastName')}
            />
            <FieldError
              id="full-lastName-error"
              message={errors.lastName?.message}
            />
          </div>
        )}

        <div>
          <label className="form-label" htmlFor={`${variant}-email`}>
            Email <span aria-hidden="true">*</span>
          </label>
          <input
            id={`${variant}-email`}
            className={inputClass}
            type="email"
            placeholder="ime@kompanija.rs"
            autoComplete="email"
            inputMode="email"
            aria-invalid={Boolean(errors.email)}
            aria-describedby={
              errors.email ? `${variant}-email-error` : undefined
            }
            {...register('email')}
          />
          <FieldError
            id={`${variant}-email-error`}
            message={errors.email?.message}
          />
        </div>

        <div>
          <label className="form-label" htmlFor={`${variant}-phone`}>
            Telefon <span aria-hidden="true">*</span>
          </label>
          <input
            id={`${variant}-phone`}
            className={inputClass}
            type="tel"
            placeholder="+381 6x xxx xxxx"
            autoComplete="tel"
            inputMode="tel"
            aria-invalid={Boolean(errors.phone)}
            aria-describedby={
              errors.phone ? `${variant}-phone-error` : undefined
            }
            {...register('phone')}
          />
          <FieldError
            id={`${variant}-phone-error`}
            message={errors.phone?.message}
          />
        </div>
      </div>

      <div className="mt-5">
        <label className="form-label" htmlFor={`${variant}-service`}>
          {isFull ? 'Odabir usluge' : 'Tema / usluga'}{' '}
          <span aria-hidden="true">*</span>
        </label>
        <select
          id={`${variant}-service`}
          className={inputClass}
          defaultValue=""
          aria-invalid={Boolean(errors.service)}
          aria-describedby={
            errors.service ? `${variant}-service-error` : undefined
          }
          {...register('service')}
        >
          <option value="" disabled>
            Izaberite opciju
          </option>
          {services.map(service => (
            <option key={service.id} value={service.id}>
              {service.title}
            </option>
          ))}
          <option value="ostalo">Nešto drugo</option>
        </select>
        <FieldError
          id={`${variant}-service-error`}
          message={errors.service?.message}
        />
      </div>

      <div className="mt-5">
        <label className="form-label" htmlFor={`${variant}-message`}>
          Poruka <span aria-hidden="true">*</span>
        </label>
        <textarea
          id={`${variant}-message`}
          className={`${inputClass} min-h-32 resize-y py-3`}
          placeholder="Ukratko nam opišite vaš cilj ili izazov..."
          aria-invalid={Boolean(errors.message)}
          aria-describedby={
            errors.message ? `${variant}-message-error` : undefined
          }
          {...register('message')}
        />
        <FieldError
          id={`${variant}-message-error`}
          message={errors.message?.message}
        />
      </div>

      <div className="mt-5">
        <div
          className={`flex items-start gap-3 text-sm leading-6 ${tone === 'dark' ? 'text-footer-muted' : 'text-text-secondary'}`}
        >
          <input
            id={`${variant}-privacy`}
            type="checkbox"
            className={`mt-1 size-4.5 shrink-0 cursor-pointer rounded-[3px] text-primary accent-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/25 ${tone === 'dark' ? 'border border-white/25 bg-white/8' : 'border border-border-strong bg-input'}`}
            aria-invalid={Boolean(errors.privacy)}
            aria-describedby={
              errors.privacy ? `${variant}-privacy-error` : undefined
            }
            {...register('privacy')}
          />
          <label className="cursor-pointer" htmlFor={`${variant}-privacy`}>
            Prihvatam{' '}
            <Link
              className={`animated-link font-medium hover:text-primary ${tone === 'dark' ? 'text-footer-text' : 'text-text-primary'}`}
              to="/kontakt#politika-privatnosti"
              onClick={event => event.stopPropagation()}
            >
              politiku privatnosti
            </Link>{' '}
            i saglasan/na sam da se navedeni podaci koriste za odgovor na upit.
          </label>
        </div>
        <FieldError
          id={`${variant}-privacy-error`}
          message={errors.privacy?.message}
        />
      </div>

      <AnimatePresence>
        {submitted && (
          <motion.div
            className="mt-5 flex items-start gap-3 rounded-[var(--radius-sm)] bg-primary-soft p-4 text-sm text-text-primary"
            role="status"
            initial={reduceMotion ? false : { opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={reduceMotion ? undefined : { opacity: 0, y: -4 }}
            transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
          >
            <CheckCircle2
              aria-hidden="true"
              className="mt-0.5 size-5 shrink-0 text-primary"
            />
            <p>
              Forma je uspešno proverena. Slanje je trenutno u demo režimu i
              spremno je za povezivanje sa API-jem.
            </p>
          </motion.div>
        )}
      </AnimatePresence>

      <Button
        className="mt-6"
        size="lg"
        type="submit"
        fullWidth={isFull}
        disabled={isSubmitting}
      >
        {isSubmitting
          ? 'Slanje...'
          : isFull
            ? 'Pošalji poruku'
            : 'Pošalji upit'}
        {!isSubmitting && <ArrowRight aria-hidden="true" className="size-4" />}
      </Button>
    </form>
  );
}
